import { JSX, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import Constants from 'style/constants';
import MonitoringAlert from 'shared/components/monitoring/monitoring-alert/monitoring-alert';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';

import Device from 'features/device/domain/models/device';
import { DeviceAlert } from 'features/device/domain/models/device-alert';
import { DeviceAlertType } from 'features/device/domain/models/device-alert-type';

interface Props {
  device?: Device;
}

const DeviceMonitoringAlerts = ({ device }: Props): ReactElement => {
  const { t } = useTranslation('monitoring');

  if (!device?.deviceAlerts) {
    return <></>;
  }

  const renderAlert = (alert: DeviceAlert): JSX.Element | undefined => {
    switch (alert.type) {
      case DeviceAlertType.DeviceOffline:
        return (
          <MonitoringAlert
            key={alert.type}
            text={t('messages.offline')}
            type={MonitoringType.Offline}
            message={alert.message}
            lastChangedOn={alert.createdOn}
            color={Constants.Colors.error}
          />
        );
      case DeviceAlertType.BatteryLow:
        return (
          <MonitoringAlert
            key={alert.type}
            text={t('messages.batteryLow')}
            type={MonitoringType.BatteryLow}
            message={alert.message}
            lastChangedOn={alert.createdOn}
            color={Constants.Colors.brightOrange}
          />
        );
      case DeviceAlertType.BatteryCritical:
      case DeviceAlertType.BatteryEmpty:
        return (
          <MonitoringAlert
            key={alert.type}
            text={t('messages.batteryCritical')}
            type={MonitoringType.BatteryCritical}
            message={alert.message}
            lastChangedOn={alert.createdOn}
            color={Constants.Colors.error}
          />
        );
      case DeviceAlertType.IS32Error:
      case DeviceAlertType.IS32LocalbusError:
      case DeviceAlertType.IS32UnitError:
        return (
          <MonitoringAlert
            key={alert.type}
            text={t('messages.deviceMalfunction')}
            type={MonitoringType.DeviceMalfunction}
            message={alert.message}
            lastChangedOn={alert.createdOn}
            color={Constants.Colors.brightOrange}
          />
        );
    }
  };

  return (
    <>
      {device.isOutdated && (
        <MonitoringAlert key="outdated" text={t('messages.outDated')} type={MonitoringType.OutDated} color={Constants.Colors.error} />
      )}
      {device.deviceAlerts.map((alert) => renderAlert(alert))}
    </>
  );
};

export default DeviceMonitoringAlerts;
